import { Tooltip, TooltipContent, TooltipTrigger } from "@aircall/ds";
import { Frown, Info, Meh, Smile } from "@aircall/react-icons";
import { useTranslation } from "react-i18next";

import { useSectionSentiment } from "./SectionSentiment.hooks";

import { Feedbacks } from "@/blocks/Feedbacks";
import { Section } from "@/blocks/Section";
import { Link } from "@/components/Link/Link";
import { useCall } from "@/hooks/useCall/useCall";

import { SentimentType } from "@/graphql/generated/graphql";
import { PickContentProps, PickIconProps } from ".";

function PickIcon({ sentiment }: PickIconProps) {
  switch (sentiment) {
    case "POSITIVE":
      return <Smile className="size-4 text-primary" />;
    case "NEGATIVE":
      return <Frown className="size-4 text-destructive" />;
    case "NEUTRAL":
      return <Meh className="size-4 text-muted-foreground" />;
    default:
      return null;
  }
}

function PickContent({ sentiment }: PickContentProps) {
  const { t } = useTranslation();

  switch (sentiment) {
    case "POSITIVE":
      return (
        <span className="text-sm font-semibold text-muted-foreground">
          {t("customer_mood.positive")}
        </span>
      );
    case "NEGATIVE":
      return (
        <span className="text-sm font-semibold text-muted-foreground">
          {t("customer_mood.negative")}
        </span>
      );
    case "NEUTRAL":
      return (
        <span className="text-sm font-semibold text-muted-foreground">
          {t("customer_mood.neutral")}
        </span>
      );
    default:
      return null;
  }
}

export function SectionSentiment() {
  const { t } = useTranslation();

  const { call } = useCall();
  const sentiment = call?.overallSentiments?.get(SentimentType.EXTERNAL);

  const {
    onClickThumbUp: handleOnClickThumbUp,
    onClickThumbUpUpdated: handleOnClickThumbUpUpdated,
    onClickThumbDown: handleOnClickThumbDown,
    onClickThumbDownUpdated: handleOnClickThumbDownUpdated,
  } = useSectionSentiment();

  if (!sentiment) {
    return null;
  }

  return (
    <Section data-test="customer-mood-card">
      <div className="flex items-center gap-1">
        <span className="text-base font-semibold">
          {t("customer_mood.title")}
        </span>
        <Tooltip>
          <TooltipTrigger render={<div data-test="customer-mood-info" />}>
            <Info className="size-4" />
          </TooltipTrigger>
          <TooltipContent side="top" align="center">
            {t("customer_mood.tooltip.content")}{" "}
            <Link href={t("customer_mood.tooltip.href_url")} target="_blank">
              {t("customer_mood.tooltip.href_title")}
            </Link>
          </TooltipContent>
        </Tooltip>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-1">
          <PickIcon sentiment={sentiment} />
          <PickContent sentiment={sentiment} />
        </div>
        <Feedbacks
          onClickThumbUp={handleOnClickThumbUp}
          onClickThumbDown={handleOnClickThumbDown}
          onClickThumbUpUpdated={handleOnClickThumbUpUpdated}
          onClickThumbDownUpdated={handleOnClickThumbDownUpdated}
        />
      </div>
    </Section>
  );
}
